import React, { useState, useEffect } from 'react';
import { Team } from '../types/index.ts';
import { topicApi } from '../services/api.ts';

interface TeamSelectionModalProps {
  onTeamSelected: (team: Team) => void;
  onClose?: () => void;
  showCreateFormDirectly?: boolean;
}

const TeamSelectionModal: React.FC<TeamSelectionModalProps> = ({ onTeamSelected, onClose, showCreateFormDirectly = false }) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(showCreateFormDirectly);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadTeams();
  }, []);

  const loadTeams = async () => {
    try {
      const teamsData = await topicApi.getTeams();
      setTeams(teamsData);
      if (teamsData.length > 0) {
        setSelectedTeamId(teamsData[0].teamId);
      }
    } catch (error) {
      setError('Failed to load series');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTeam = () => {
    const selectedTeam = teams.find(team => team.teamId === selectedTeamId);
    if (selectedTeam) {
      onTeamSelected(selectedTeam);
    }
  };

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newTeamName.trim()) {
      setError('Series name is required');
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      const response = await topicApi.createTeam(newTeamName.trim());
      if (response.success && response.data) {
        onTeamSelected(response.data);
      } else {
        setError(response.error || 'Failed to create series');
        setIsCreating(false);
      }
    } catch (error: any) {
      setError('Failed to create series');
      setIsCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="loading">Loading series...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h3>Select Your Series</h3>
        
        {!showCreateForm ? (
          <div>
            {teams.length > 0 ? (
              <div>
                <div className="form-group">
                  <label>Choose your series:</label>
                  <select
                    value={selectedTeamId}
                    onChange={(e) => setSelectedTeamId(e.target.value)}
                    style={{ width: '100%', padding: '8px', marginBottom: '16px' }}
                  >
                    {teams.map((team) => (
                      <option key={team.teamId} value={team.teamId}>
                        {team.teamName}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="modal-actions">
                  <button onClick={handleSelectTeam} className="primary-button">
                    Continue with Selected Series
                  </button>
                  <button 
                    onClick={() => setShowCreateForm(true)}
                    className="secondary-button"
                  >
                    Create New Series
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p>No series exist yet. Create the first series to get started.</p>
                <button 
                  onClick={() => setShowCreateForm(true)}
                  className="primary-button"
                >
                  Create New Series
                </button>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleCreateTeam}>
            <div className="form-group">
              <label>New Series Name:</label>
              <input
                type="text"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                placeholder="Enter series name"
                required
                autoFocus
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="modal-actions">
              <button type="submit" disabled={isCreating}>
                {isCreating ? 'Creating...' : 'Create Series'}
              </button>
              {teams.length > 0 && (
                <button 
                  type="button" 
                  onClick={() => {
                    if (onClose) {
                      onClose();
                    } else {
                      setShowCreateForm(false);
                      setError('');
                      setNewTeamName('');
                    }
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default TeamSelectionModal;
